import React from 'react'
import {Box, Spinner} from '../..'
import {RuleItemProps} from './types'

// InnerVisualSlot - can house a leading visual, trailing visual, counter, and loading spinners
// ------------------------
export interface InnerVisualSlotProps {
  /** */
  children?: React.ReactNode
  /** Whether the Rule Item is expected to ever show a loading indicator */
  hasLoadingIndicator: boolean
  /** Whether to show the loading indicator */
  showLoadingIndicator: RuleItemProps['loading']
  /** Whether to render in leading or trailing position */
  visualPosition: 'leading' | 'trailing'
}

/**
 *
 * Inner visual slot to house leading visual, trailing visual, counter, and loading spinners
 *
 */
const RuleItemInnerVisualSlot = ({
  children,
  hasLoadingIndicator,
  showLoadingIndicator,
  visualPosition
}: InnerVisualSlotProps) => {
  // if there is no inner visual and the Rule Item will never show a loading indicator
  // OR
  // if the visual should render in the leading position, but has neither children nor a loading indicator, return null
  if ((!children && !hasLoadingIndicator) || (visualPosition === 'leading' && !children && !showLoadingIndicator)) {
    return null
  }

  // if the Rule Item will never display a loading indicator, just render the visual
  if (!hasLoadingIndicator) {
    return (
      <span className="rule-item-visual">
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>{children}</Box>
      </span>
    )
  }

  // else, do this:
  // is the Rule Item in a loading state? If yes, show a spinner in the visual position based on the loadingPosition prop; if not, just render the visual.
  return (
    <span className="rule-item-visual">
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, position: 'relative'}}>
        {children && !showLoadingIndicator && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>{children}</Box>
        )}
        {children && showLoadingIndicator && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Spinner
              aria-label="Loading"
              sx={
                children
                  ? {
                      position: 'absolute',
                      top: 0,
                      height: '100%',
                      maxWidth: '100%',
                      visibility: 'visible',
                      ...(visualPosition === 'leading' ? {left: '0px'} : {right: '0px'})
                    }
                  : {visibility: 'visible'}
              }
              size={children ? undefined : 'small'}
            />
          </Box>
        )}
      </Box>
    </span>
  )
}
export default RuleItemInnerVisualSlot
